import { MaskedSerial } from '../../components/asset-detail/MaskedSerial';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { type ColumnDef } from '@tanstack/react-table';
import { ChevronRight, Boxes, MapPin, MapPinOff } from 'lucide-react';
import { api, authFetch, API_BASE, throwIfNotOk } from '../../lib/api';
import type { AssetListResponse } from '../../lib/types';
import { qk } from '../../lib/queryKeys';
import { useAuth } from '../../lib/authContext';
import { useOrgFeature, OrgFeature } from '../../lib/orgFeatures';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { LifecycleBadge } from '@siliconx/shared/ui/LifecycleBadge';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { EmptyState } from '@siliconx/shared/ui/state';
import { Field } from '@siliconx/shared/ui/Field';
import { Select } from '@siliconx/shared/ui/Select';
import { ManufacturerLogo } from '../../components/shared/ManufacturerLogo';
import { ComponentLinks } from '../../components/assets/ComponentLinks';
import { FmvCell } from '../../components/assets/FmvCell';
import { customsReasonCopy, toReviewReason } from '../../components/assets/CustomsReason';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import {
  TelemetryStatusDot,
  FinancialsStatusBadge,
  type TelemetryStatus,
  type FinancialsStatus,
  type FinancialsSource,
} from '../../components/assets/AssetStatusBadges';
import { BulkManualEntryModal } from '../../components/assets/BulkManualEntryModal';
import { BULK_ENTRY_MAX_COUNT } from '@siliconx/shared';
import { FilterBar, ResultCountMeta, ChipRow } from './FilterBar';
import { SearchField } from './FilterFields';
import { MobileFilterSheet } from './MobileFilterSheet';
import { useDebouncedValue } from './useDebouncedValue';
import type { ActiveChip, FilterBarProps, FilterOption } from './filterTypes';

/**
 * /assets — the fleet list, grouped by asset class -> location. Filter
 * state lives in the URL (zod `validateSearch`, mirroring the ops
 * `/assets` and `/skus` pattern) so a filtered view is shareable, survives
 * a reload, and works with back/forward — the prototype this was built
 * from (`_prototype/FilterVariantA.tsx` on `jarriett/proto-assets-filters`)
 * used local `useState` and lost all of that on every navigation.
 *
 * See plans/filter-standardization-spec.md for the design rationale (§1-2
 * amended in this PR: this route has no "asset class" FILTER — class only
 * drives the grouping below — so manufacturer, not class, is the 4th
 * primary control).
 */

// Compact USD for group subtotals — formatUsd in UsdCell isn't exported.
const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const LIFECYCLE_OPTIONS: FilterOption[] = [
  { value: '', label: 'All states' },
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'decommissioned', label: 'Decommissioned' },
  { value: 'disposed', label: 'Disposed' },
  { value: 'quarantined', label: 'In Customs' },
];

const CONDITION_OPTIONS: FilterOption[] = [
  { value: '', label: 'All conditions' },
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like new' },
  { value: 'normal_enterprise_wear', label: 'Normal wear' },
  { value: 'high_wear', label: 'High wear' },
  { value: 'defective', label: 'Defective' },
  { value: 'unknown', label: 'Unknown' },
];

type GroupSort = 'name' | 'fmv' | 'count';

const SORT_OPTIONS: { value: GroupSort; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'fmv', label: 'FMV total' },
  { value: 'count', label: 'Asset count' },
];

// -- URL search state ---------------------------------------------------
// `missing_financials` is deliberately `z.enum(['true'])`, not
// `z.coerce.boolean()` — zod's coerce.boolean() runs `Boolean(value)` on
// whatever string lands in the URL, so `?missing_financials=false` would
// coerce to `true`. This route only ever WRITES `'true'` (the setter omits
// the key entirely for `false`), so presence-of-key is the only signal that
// matters and the pitfall never comes up.
const assetsSearchSchema = z.object({
  lifecycle_state: z.string().optional(),
  condition: z.string().optional(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  data_center_id: z.string().optional(),
  lot_id: z.string().optional(),
  team_id: z.string().optional(),
  missing_financials: z.enum(['true']).optional(),
  q: z.string().optional(),
  sort: z.enum(['name', 'fmv', 'count']).optional(),
});
type AssetsSearch = z.infer<typeof assetsSearchSchema>;

export const Route = createFileRoute('/assets/')({
  validateSearch: (search) => assetsSearchSchema.parse(search),
  component: AssetsPage,
});

// ---------------------------------------------------------------------------
// ESG CSV export (SIL-192) — streams the fleet-wide embodied-carbon CSV from
// GET /api/client/export/carbon.csv. apiFetch (not a bare <a href>) so the
// Cognito ID token rides along; the response is turned into a blob download.
// ---------------------------------------------------------------------------
function ExportEsgCsvButton() {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`${API_BASE}/api/client/export/carbon.csv`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fleet-embodied-carbon.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[esg-export] download failed:', err);
      window.alert('ESG CSV export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleExport()}
      disabled={exporting}
      className="cursor-pointer rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {exporting ? 'Exporting…' : 'Export ESG CSV'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssetRow {
  id: string;
  /** SIL-1269: presence only; the serial is fetched on Reveal. */
  hasSerial: boolean;
  /** SIL-1648: 1 = one serialized unit; > 1 = a stock line (no serial/asset tag). */
  quantity: number;
  /** SIL-1648: per-unit FMV x quantity; null when unpriced. */
  lineValueUsd: number | null;
  /** SIL-1205: 'unavailable' means the device was asked for a serial and
   * reported none (e.g. a GeForce board's [N/A] to nvidia-smi) - distinct
   * from null, which means no probe ever captured this asset's identity. */
  hardwareSerialSource: string | null;
  assetClass: string;
  manufacturerId: string | null;
  manufacturerName: string | null;
  modelName: string | null;
  skuMatchStatus: string | null;
  sellerManufacturerRaw: string | null;
  sellerModelRaw: string | null;
  condition: string | null;
  lifecycleState: string;
  /** SIL-1205: raw lifecycle_state id (e.g. 'quarantined') - `lifecycleState`
   * is the display name and can't be used to key rendering. */
  lifecycleStateId: string | null;
  /** SIL-1205: machine-readable reason this asset is in Customs (or its
   * parent chassis's reason, for a component) - see CustomsReason.tsx. */
  customsReviewReason: string | null;
  fmvMedianUsd: number | null;
  teamId: string | null;
  dataCenterId: string | null;
  dataCenterName: string | null;
  /** Epic 1 (lots-first-class): the lot this asset belongs to, if any. */
  lotId: string | null;
  createdAt: string | null;
  financialsStatus: FinancialsStatus;
  financialsSource: FinancialsSource;
  financialsSourceSystem: string | null;
  telemetryStatus: TelemetryStatus;
  telemetryLastSeenAt: string | null;
  // Probe asset grouping (contract gap G10).
  parentAssetId: string | null;
  parentLabel: string | null;
  /** SIL-1202: parentLabel is a fixed placeholder ("Unidentified chassis") when true. */
  parentIsUnidentifiedChassis: boolean;
  componentCount: number;
  /** D8 (contract gap G11): fmvMedianUsd is the sum of priced components. */
  fmvEstimatedFromComponents: boolean;
}

interface Team {
  id: string;
  name: string;
}

interface LocationOption {
  id: string;
  code: string;
  name: string;
}

/** Epic 1 (lots-first-class) — minimal shape for the filter dropdown + column lookup. */
interface LotOption {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const BASE_COLUMNS: ColumnDef<AssetRow, unknown>[] = [
  {
    id: 'serialNumber',
    accessorFn: (row) => row.hasSerial,
    enableSorting: false,
    header: 'Serial number',
    cell: (ctx) => {
      // SIL-1648: a stock line has no serial; its quantity shows in the FMV cell.
      if (isLine(ctx.row.original)) return null;
      if (ctx.row.original.hasSerial) {
        return <MaskedSerial assetId={ctx.row.original.id} hasSerial className="font-mono text-xs text-zinc-700" />;
      }
      // SIL-1205: tell "the device was asked and reported none" (e.g. a
      // GeForce board's [N/A] to nvidia-smi, serialSource 'unavailable')
      // apart from "no probe data at all" - only the former gets an
      // explicit sentence; the latter stays the plain em dash.
      if (ctx.row.original.hardwareSerialSource === 'unavailable') {
        return <span className="text-xs italic text-zinc-500">Not reported by device</span>;
      }
      return <span className="font-mono text-xs text-zinc-700">—</span>;
    },
  },
  {
    accessorKey: 'manufacturerName',
    header: 'Manufacturer',
    cell: (ctx) => (
      <span className="text-zinc-800">
        <ManufacturerLogo
          id={ctx.row.original.manufacturerId}
          name={ctx.row.original.manufacturerName}
          size={16}
          slot
        />
      </span>
    ),
  },
  {
    accessorKey: 'modelName',
    header: 'Model',
    cell: (ctx) => {
      const modelName = ctx.getValue<string | null>();
      const row = ctx.row.original;
      // SIL-1205: an unmatched asset still has a probe-reported model name
      // (seller_model_raw) even with no SKU match — show it as a raw value
      // marked unmatched, not a blank "—".
      const rawModel = !modelName && row.skuMatchStatus === 'unmatched' ? row.sellerModelRaw : null;
      return (
        <div className="min-w-0">
          {modelName ? (
            <span className="text-zinc-800">{modelName}</span>
          ) : rawModel ? (
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-zinc-800">{rawModel}</span>
              <span className="rounded-sm bg-amber-50 px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
                Unmatched
              </span>
            </span>
          ) : (
            <span className="text-zinc-800">—</span>
          )}
          <ComponentLinks
            assetId={row.id}
            componentCount={row.componentCount}
            parentAssetId={row.parentAssetId}
            parentLabel={row.parentLabel}
            parentIsUnidentifiedChassis={row.parentIsUnidentifiedChassis}
          />
        </div>
      );
    },
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: (ctx) => <ConditionBadge value={ctx.getValue<string | null>()} />,
  },
  {
    accessorKey: 'lifecycleState',
    header: 'Status',
    cell: (ctx) => {
      const row = ctx.row.original;
      const stateId = row.lifecycleStateId ?? ctx.getValue<string>();
      // SIL-1205: the asset list uses the same name for Customs as the
      // add-asset wizard's step-2 copy ("added automatically … anything
      // else lands in Customs") - "Quarantined" never appears here.
      const label = stateId === 'quarantined' ? 'In Customs' : ctx.getValue<string>();
      const inCustoms = stateId === 'quarantined';
      const reason = inCustoms ? customsReasonCopy(toReviewReason(row.customsReviewReason), row.id) : null;
      return (
        <div className="min-w-0 max-w-[16rem] space-y-1">
          <LifecycleBadge id={stateId} label={label} />
          {reason && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {reason.sentence}{' '}
              {reason.action.kind === 'contact-support' ? (
                <a
                  href={reason.action.mailto}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="font-medium underline hover:text-amber-900"
                >
                  {reason.actionLabel}
                </a>
              ) : (
                <Link
                  to="/customs"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="font-medium underline hover:text-amber-900"
                >
                  {reason.actionLabel}
                </Link>
              )}
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'fmvMedianUsd',
    header: 'FMV (USD)',
    cell: (ctx) => (
      <span className="inline-flex flex-col items-end">
        <FmvCell
          value={lineValue(ctx.row.original)}
          estimatedFromComponents={ctx.row.original.fmvEstimatedFromComponents}
        />
        {isLine(ctx.row.original) && (
          <span className="text-xs text-zinc-500">Qty {ctx.row.original.quantity.toLocaleString()}</span>
        )}
      </span>
    ),
  },
  {
    id: 'telemetry',
    header: 'Telemetry',
    cell: (ctx) => <TelemetryStatusDot status={ctx.row.original.telemetryStatus} />,
  },
  {
    id: 'financials',
    header: 'Financials',
    cell: (ctx) => (
      <FinancialsStatusBadge
        status={ctx.row.original.financialsStatus}
        source={ctx.row.original.financialsSource}
        sourceSystem={ctx.row.original.financialsSourceSystem}
      />
    ),
  },
];

/** Epic 1 (lots-first-class) — "Lot" column. Built as a factory (not a static
 *  entry in BASE_COLUMNS) because the cell needs the loaded `lots` list to
 *  resolve a name from lotId, same pattern as the admin-only team column
 *  below. Shown to every seller (lot membership isn't privilege-gated). */
function makeLotColumn(lots: LotOption[] | undefined): ColumnDef<AssetRow, unknown> {
  return {
    id: 'lot',
    header: 'Lot',
    cell: (ctx) => {
      const row = ctx.row.original;
      const name = lots?.find((l) => l.id === row.lotId)?.name;
      return <span className="text-xs text-zinc-700">{row.lotId ? (name ?? '—') : '—'}</span>;
    },
  };
}

// ---------------------------------------------------------------------------
// Grouping: asset class → location
// ---------------------------------------------------------------------------

const NO_LOCATION = 'No location';

interface LocationGroup {
  label: string;
  isNoLocation: boolean;
  rows: AssetRow[];
  count: number;
  fmvTotal: number;
}

interface ClassGroup {
  name: string;
  locations: LocationGroup[];
  count: number;
  fmvTotal: number;
}

/** Spec section 4: group totals count top-level assets only, so a search
 *  that returns a server and its GPU counts one asset, valued once. */
const isTopLevel = (r: AssetRow) => !r.parentAssetId;
/** SIL-1648: a row with quantity > 1 is a stock line, valued per line. */
const isLine = (r: AssetRow) => (r.quantity ?? 1) > 1;
const lineValue = (r: AssetRow) => (isLine(r) ? r.lineValueUsd : r.fmvMedianUsd);
const unitsOf = (r: AssetRow) => (isTopLevel(r) ? (r.quantity ?? 1) : 0);
const fmvOf = (r: AssetRow) => (isTopLevel(r) ? (lineValue(r) ?? 0) : 0);

/** Group rows by asset class, then location; sort both levels by `sort`.
 *  "No location" is always pinned last within its class. */
export function groupAssets(rows: AssetRow[], sort: GroupSort): ClassGroup[] {
  const byClass = new Map<string, Map<string, AssetRow[]>>();
  for (const r of rows) {
    const loc = r.dataCenterName ?? NO_LOCATION;
    if (!byClass.has(r.assetClass)) byClass.set(r.assetClass, new Map());
    const locs = byClass.get(r.assetClass)!;
    if (!locs.has(loc)) locs.set(loc, []);
    locs.get(loc)!.push(r);
  }

  const cmp = (a: { name: string; count: number; fmvTotal: number }, b: typeof a) => {
    if (sort === 'fmv') return b.fmvTotal - a.fmvTotal;
    if (sort === 'count') return b.count - a.count;
    return a.name.localeCompare(b.name);
  };

  const classes: ClassGroup[] = [];
  for (const [name, locs] of byClass) {
    const locations: LocationGroup[] = [];
    for (const [label, groupRows] of locs) {
      locations.push({
        label,
        isNoLocation: label === NO_LOCATION,
        rows: groupRows,
        count: groupRows.reduce((s, r) => s + unitsOf(r), 0),
        fmvTotal: groupRows.reduce((s, r) => s + fmvOf(r), 0),
      });
    }
    locations.sort((a, b) => {
      if (a.isNoLocation !== b.isNoLocation) return a.isNoLocation ? 1 : -1;
      return cmp(
        { name: a.label, count: a.count, fmvTotal: a.fmvTotal },
        { name: b.label, count: b.count, fmvTotal: b.fmvTotal },
      );
    });
    classes.push({
      name,
      locations,
      count: locations.reduce((s, l) => s + l.count, 0),
      fmvTotal: locations.reduce((s, l) => s + l.fmvTotal, 0),
    });
  }
  classes.sort(cmp);
  return classes;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function AssetsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.role === 'admin';
  // SIL-1027/SIL-1034: an individual customer_org has no lots (GET
  // /api/client/lots is orgFeature: 'lots'-gated, clientAccess.ts) — hide
  // the filter/column entirely rather than let the query 403.
  const canLots = useOrgFeature('lots');
  const canTeams = useOrgFeature('teams');
  const canLocations = useOrgFeature('locations');
  const canFinance = useOrgFeature('finance');
  const queryClient = useQueryClient();

  // URL-search mutator. `replace: true` keeps history clean when typing —
  // used for the debounced search/model commits below.
  const setSearch = (next: Partial<AssetsSearch>, replace = false) => {
    navigate({
      search: (prev) => ({ ...prev, ...next }),
      replace,
    });
  };

  // Search/model are debounced locally before they ever reach the URL (and
  // therefore the query key) — typing a 4-character query used to fire 4
  // full cursor-exhaust reloads with no in-flight cue (Impeccable
  // 2026-08-12 P1 on the prototype). `searchDraft`/`modelDraft` are the
  // immediately-controlled input values; the debounced commit writes to
  // the URL only once input has settled, and a `searchDraft`/`modelDraft`
  // stays connected to it, and a sync-back effect below re-derives the
  //  draft whenever the URL changes from elsewhere (chip removal, Clear
  // all, browser back/forward) so the input never goes stale.
  const [searchDraft, setSearchDraft] = useState(search.q ?? '');
  const [modelDraft, setModelDraft] = useState(search.model ?? '');
  const debouncedSearch = useDebouncedValue(searchDraft, 300);
  const debouncedModel = useDebouncedValue(modelDraft, 300);

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed !== (search.q ?? '')) setSearch({ q: trimmed || undefined }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the debounced value settles
  }, [debouncedSearch]);

  useEffect(() => {
    const trimmed = debouncedModel.trim();
    if (trimmed !== (search.model ?? '')) setSearch({ model: trimmed || undefined }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the debounced value settles
  }, [debouncedModel]);

  useEffect(() => {
    setSearchDraft(search.q ?? '');
  }, [search.q]);
  useEffect(() => {
    setModelDraft(search.model ?? '');
  }, [search.model]);

  const groupSort: GroupSort = search.sort ?? 'name';

  const missingFinancialsOnly = search.missing_financials === 'true';

  const baseFilterQuery: Record<string, string> = {};
  if (search.lifecycle_state) baseFilterQuery.lifecycle_state = search.lifecycle_state;
  if (search.condition) baseFilterQuery.condition = search.condition;
  if (search.data_center_id) baseFilterQuery.data_center_id = search.data_center_id;
  if (search.team_id) baseFilterQuery.team_id = search.team_id;
  if (search.lot_id) baseFilterQuery.lot_id = search.lot_id;
  if (search.manufacturer) baseFilterQuery.manufacturer = search.manufacturer;
  if (search.model) baseFilterQuery.model = search.model;
  if (search.q) baseFilterQuery.q = search.q;
  if (missingFinancialsOnly) baseFilterQuery.missing_financials = 'true';
  const filterKey = new URLSearchParams(baseFilterQuery).toString();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  // Grouping needs the whole filtered set, but the endpoint caps limit at 200.
  // Loop the keyset cursor until exhausted, then group client-side.
  const assetsQuery = useQuery<AssetRow[]>({
    queryKey: qk.assets.listAll(filterKey),
    queryFn: async () => {
      const rows: AssetRow[] = [];
      let cursor: string | null = null;
      do {
        const res = await api.api.client.assets.$get({
          query: { ...baseFilterQuery, limit: '200', ...(cursor ? { cursor } : {}) },
        });
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        const page = (await res.json()) as unknown as AssetListResponse<AssetRow>;
        rows.push(...page.data);
        cursor = page.meta.nextCursor;
      } while (cursor);
      return rows;
    },
  });
  const allAssets = assetsQuery.data;

  // Manufacturer dropdown derived client-side from the loaded set, same
  // approach as the ops SKU list — no dedicated /api/client/manufacturers endpoint.
  const manufacturerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of allAssets ?? []) {
      if (row.manufacturerName) names.add(row.manufacturerName);
    }
    return [...names].sort();
  }, [allAssets]);

  // Teams for the filter dropdown + (admin-only) per-asset assignment dropdown (RUF-380).
  const { data: teams } = useQuery<Team[]>({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as Team[];
    },
    enabled: isAdmin && canTeams,
  });

  // Locations for the filter dropdown (RUF-448).
  // SIL-1035/SIL-1034: an individual customer_org has no location concept —
  // GET /api/client/locations is orgFeature: 'locations'-gated and would
  // 403; only fetch when the org unlocks it.
  const { data: locations } = useQuery<LocationOption[]>({
    queryKey: qk.locations(),
    queryFn: async () => {
      const res = await api.api.client.locations.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LocationOption[];
    },
    enabled: canLocations,
  });

  // Lots for the filter dropdown + column lookup (Epic 1, lots-first-class).
  // Available to any seller — mirrors the location filter, not team-gated.
  // SIL-1027/SIL-1034: disabled for an org that doesn't unlock 'lots' — the
  // API is orgFeature: 'lots'-gated and would 403.
  const { data: lots } = useQuery<LotOption[]>({
    queryKey: qk.lots.all,
    queryFn: async () => {
      const res = await api.api.client.lots.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LotOption[];
    },
    enabled: canLots,
  });

  // -- Active-filter chips, individually removable ------------------------
  const activeChips = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];
    if (search.q) {
      chips.push({
        key: 'q',
        label: 'Search',
        value: search.q,
        onRemove: () => setSearch({ q: undefined }),
      });
    }
    if (search.lifecycle_state) {
      const label =
        LIFECYCLE_OPTIONS.find((o) => o.value === search.lifecycle_state)?.label ??
        search.lifecycle_state;
      chips.push({
        key: 'lifecycle_state',
        label: 'Lifecycle',
        value: label,
        onRemove: () => setSearch({ lifecycle_state: undefined }),
      });
    }
    if (search.condition) {
      const label =
        CONDITION_OPTIONS.find((o) => o.value === search.condition)?.label ?? search.condition;
      chips.push({
        key: 'condition',
        label: 'Condition',
        value: label,
        onRemove: () => setSearch({ condition: undefined }),
      });
    }
    if (search.manufacturer) {
      chips.push({
        key: 'manufacturer',
        label: 'Manufacturer',
        value: search.manufacturer,
        onRemove: () => setSearch({ manufacturer: undefined }),
      });
    }
    if (search.model) {
      chips.push({
        key: 'model',
        label: 'Model',
        value: search.model,
        onRemove: () => setSearch({ model: undefined }),
      });
    }
    if (search.data_center_id && canLocations) {
      const label =
        locations?.find((l) => l.id === search.data_center_id)?.name ?? search.data_center_id;
      chips.push({
        key: 'data_center_id',
        label: 'Location',
        value: label,
        onRemove: () => setSearch({ data_center_id: undefined }),
      });
    }
    if (search.lot_id && canLots) {
      const label = lots?.find((l) => l.id === search.lot_id)?.name ?? search.lot_id;
      chips.push({
        key: 'lot_id',
        label: 'Lot',
        value: label,
        onRemove: () => setSearch({ lot_id: undefined }),
      });
    }
    if (search.team_id && canTeams) {
      const label = teams?.find((t) => t.id === search.team_id)?.name ?? search.team_id;
      chips.push({
        key: 'team_id',
        label: 'Team',
        value: label,
        onRemove: () => setSearch({ team_id: undefined }),
      });
    }
    if (missingFinancialsOnly && canFinance) {
      chips.push({
        key: 'missing_financials',
        label: 'Missing financials',
        value: 'Yes',
        onRemove: () => setSearch({ missing_financials: undefined }),
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearch is a stable closure over navigate
  }, [search, locations, lots, teams, missingFinancialsOnly, canLots, canLocations, canTeams, canFinance]);

  const onClearAll = () => {
    navigate({ search: (prev) => (prev.sort ? { sort: prev.sort } : {}) });
  };

  const filterBarProps: FilterBarProps = {
    searchDraft,
    setSearchDraft,
    lifecycleFilter: search.lifecycle_state ?? '',
    setLifecycleFilter: (v) => {
      setSearch({ lifecycle_state: v || undefined });
    },
    conditionFilter: search.condition ?? '',
    setConditionFilter: (v) => {
      setSearch({ condition: v || undefined });
    },
    manufacturerFilter: search.manufacturer ?? '',
    setManufacturerFilter: (v) => {
      setSearch({ manufacturer: v || undefined });
    },
    modelDraft,
    setModelDraft,
    locationFilter: search.data_center_id ?? '',
    setLocationFilter: (v) => {
      setSearch({ data_center_id: v || undefined });
    },
    lotFilter: search.lot_id ?? '',
    setLotFilter: (v) => {
      setSearch({ lot_id: v || undefined });
    },
    teamFilter: search.team_id ?? '',
    setTeamFilter: (v) => {
      setSearch({ team_id: v || undefined });
    },
    missingFinancialsOnly,
    setMissingFinancialsOnly: (v) => {
      setSearch({ missing_financials: v ? 'true' : undefined });
    },

    lifecycleOptions: LIFECYCLE_OPTIONS,
    conditionOptions: CONDITION_OPTIONS,
    manufacturerOptions,
    locations,
    lots,
    teams,
    isAdmin,

    resultCount: allAssets?.length ?? 0,
    isFetching: assetsQuery.isFetching,
    activeChips,
    onClearAll,
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // SIL-305: bulk-select checkbox column - admin-only (member has no bulk
  // action to select for; "server 403 boundary" is the write route, this is
  // just not offering a UI a member can't use). stopPropagation keeps the
  // row's onRowClick nav from firing when the checkbox itself is clicked.
  const selectColumn: ColumnDef<AssetRow, unknown> = {
    id: 'select',
    header: '',
    size: 32,
    cell: (ctx) => (
      <input
        type="checkbox"
        checked={selectedIds.has(ctx.row.original.id)}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={() => {
          toggleSelected(ctx.row.original.id);
        }}
        aria-label={`Select asset ${ctx.row.original.id}`}
      />
    ),
  };

  const columns = useMemo<ColumnDef<AssetRow, unknown>[]>(() => {
    // Epic 1 (lots-first-class): lot column is shown to every seller, not
    // just admins — lot membership isn't privilege-gated the way team is.
    // SIL-1027: omitted entirely for an individual org — there are no lots.
    const lotColumns = canLots ? [makeLotColumn(lots)] : [];
    if (!isAdmin) return [...BASE_COLUMNS, ...lotColumns];
    // SIL-1035/SIL-1034: an individual customer_org has no team concept —
    // omit the Team column entirely rather than show an always-empty
    // "Unassigned" column.
    const teamColumn: ColumnDef<AssetRow, unknown> = {
      id: 'team',
      header: 'Team',
      // RUF-527: display-only — team assignment lives on the asset detail page.
      cell: (ctx) => {
        const row = ctx.row.original;
        const name = teams?.find((t) => t.id === row.teamId)?.name;
        return <span className="text-xs text-zinc-700">{name ?? '—'}</span>;
      },
    };
    const teamColumns = canTeams ? [teamColumn] : [];
    return [selectColumn, ...BASE_COLUMNS, ...teamColumns, ...lotColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectColumn closes over selectedIds intentionally
  }, [isAdmin, canLots, canTeams, teams, lots, selectedIds]);

  if (authLoading)
    return <div aria-busy="true" className="h-40 animate-pulse rounded-md bg-accent" />;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <PageHeader title="Assets" pinTo="/assets" />
          <div className="flex items-center gap-2">
            {/* Fleet embodied-carbon CSV for sustainability reporting (SIL-192).
                SIL-1035: business-only — GET /api/client/export/carbon.csv is
                orgFeature: 'sustainability'-gated. */}
            <OrgFeature name="sustainability">
              <ExportEsgCsvButton />
            </OrgFeature>
            {/* Bulk CSV import now lives in the Add asset method hub (SIL-212). */}
            <Link
              to="/assets/submit"
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              Add asset
            </Link>
          </div>
        </div>

        {/* Desktop/tablet filter bar */}
        <FilterBar {...filterBarProps} />

        {/* Mobile trigger row — search stays visible, everything else behind the sheet */}
        <div className="flex items-end gap-2 sm:hidden">
          <div className="flex-1">
            <SearchField {...filterBarProps} className="w-full" />
          </div>
          <MobileFilterSheet {...filterBarProps} />
        </div>
        <div className="sm:hidden">
          <ResultCountMeta {...filterBarProps} />
          <ChipRow {...filterBarProps} />
        </div>

        {/* SIL-305: bulk selection bar - admin-only (server 403 boundary backs
          the "Enter manually" write; members never see either action). */}
        {isAdmin && selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-brand-200 bg-brand-50 px-4 py-2.5">
            <span className="text-sm font-medium text-brand-800">{selectedIds.size} selected</span>
            {/* SIL-1035: an individual customer_org has no ERP register to
                import from — 'finance' gates financials/*, erp-register/*. */}
            <OrgFeature name="finance">
              <Link
                to="/integrations"
                className="text-sm font-medium text-brand-700 underline hover:text-brand-900"
              >
                Import from ERP…
              </Link>
            </OrgFeature>
            <Button
              className="px-3 py-1"
              onClick={() => {
                setBulkModalOpen(true);
              }}
            >
              Enter manually
            </Button>
            {selectedIds.size > BULK_ENTRY_MAX_COUNT && (
              <span className="text-xs font-medium text-amber-700">
                Bulk manual entry is capped at {BULK_ENTRY_MAX_COUNT} assets - narrow your
                selection.
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                setSelectedIds(new Set());
              }}
              className="ml-auto text-xs text-zinc-500 hover:text-zinc-700"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Results header — sort is a view/display concern (it reorders, it
          doesn't narrow the result set), so it lives here, not in the
          filter bar (DataTable.tsx:13's "pagination/sorting belong to the
          route" principle, extended to the visual layer too). */}
        <div className="flex items-center justify-end">
          <Field label="Sort groups by" className="w-44">
            <Select
              className="w-full"
              value={groupSort}
              onChange={(e) => {
                setSearch({ sort: e.target.value as GroupSort });
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <QueryBoundary
          query={assetsQuery}
          errorTitle="Couldn't load assets."
          loadingRows={10}
          empty={(rows) => groupAssets(rows, groupSort).length === 0}
          emptyState={
            <EmptyState
              title="No assets match these filters"
              description="Try widening your filters, or clear them to see your full fleet."
              action={
                <button
                  type="button"
                  onClick={onClearAll}
                  className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  Clear all
                </button>
              }
            />
          }
        >
          {(rows) => {
            const groups = groupAssets(rows, groupSort);
            return (
              <div className="space-y-3">
                {groups.map((cls) => (
                  <details
                    key={cls.name}
                    open
                    className="group/cls overflow-hidden rounded-md border border-zinc-200 bg-white"
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-2 border-b border-transparent bg-zinc-50 px-4 py-3 text-base font-semibold text-zinc-900 hover:bg-zinc-100 group-open/cls:border-zinc-100">
                      <ChevronRight className="size-4 shrink-0 text-zinc-400 transition-transform group-open/cls:rotate-90" />
                      <Boxes className="size-5 shrink-0 text-brand-600" />
                      <span>{cls.name}</span>
                      <GroupMeta count={cls.count} fmvTotal={cls.fmvTotal} />
                    </summary>
                    <div className="space-y-2 px-3 py-3">
                      {cls.locations.map((loc) => (
                        <details
                          key={loc.label}
                          open
                          className="group/loc rounded-md border border-zinc-100"
                        >
                          <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
                            <ChevronRight className="size-4 shrink-0 text-zinc-400 transition-transform group-open/loc:rotate-90" />
                            {loc.isNoLocation ? (
                              <MapPinOff className="size-4 shrink-0 text-zinc-300" />
                            ) : (
                              <MapPin className="size-4 shrink-0 text-zinc-400" />
                            )}
                            <span className={loc.isNoLocation ? 'text-zinc-400' : ''}>
                              {loc.label}
                            </span>
                            <GroupMeta count={loc.count} fmvTotal={loc.fmvTotal} />
                          </summary>
                          <div className="px-2 pb-2">
                            <DataTable
                              data={loc.rows}
                              columns={columns}
                              onRowClick={(row) =>
                                navigate({ to: '/assets/$assetId', params: { assetId: row.id } })
                              }
                            />
                          </div>
                        </details>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            );
          }}
        </QueryBoundary>
      </div>

      {bulkModalOpen && (
        <BulkManualEntryModal
          assetIds={[...selectedIds]}
          onClose={() => {
            setBulkModalOpen(false);
          }}
          onApplied={() => {
            void queryClient.invalidateQueries({ queryKey: qk.assets.listAll() });
            setSelectedIds(new Set());
          }}
        />
      )}
    </>
  );
}

/** Count + FMV subtotal shown in each accordion header. */
function GroupMeta({ count, fmvTotal }: { count: number; fmvTotal: number }) {
  return (
    <span className="ml-auto flex items-center gap-3 text-xs font-normal text-zinc-500">
      <span>
        {count} {count === 1 ? 'asset' : 'assets'}
      </span>
      <span className="tabular-nums">{usdCompact.format(fmvTotal)}</span>
    </span>
  );
}
